import type { Scene } from '@babylonjs/core/scene';

import { armourProfile, buildArmour, buildHead, buildLimbs, headStyleFor } from './armour';
import { buildOffHand } from './offhand';
import type { ModelPalette } from './palette';
import { createRig, HUMAN_LIMBS, type Rig, sphere } from './rig';
import type { WornGear } from './types';
import { buildWeapon, weaponProfile } from './weapons';

/** Hip height. Everything is measured from here so the feet land on y = 0. */
const HIP_Y = 0.86;

/**
 * How far the face plate is tilted up out of vertical, in radians.
 *
 * Half the camera's own pitch. Derived at the call site, and derived rather than eyeballed
 * because it is a fact about `CAMERA_BETA` — if the camera ever tilts, this is wrong with it.
 * `CAMERA_BETA` is π/4, so the view arrives 45° above the horizon; a face painted on a
 * vertical plane is seen at that same 45° and loses a third of its area to foreshortening
 * (cos 45° = 0.71) even when the character is looking straight at you. At 0.35 rad the
 * normal sits 20° up, the view meets it at 25°, and the face keeps 0.91 of its area. Not
 * the full 45°, because this plane is also how a player reads which way they are facing,
 * and a chin pointing at the horizon reads as a bowed head.
 */
const FACE_TILT = 0.35;

/**
 * The Dark Crusader — the player.
 *
 * A hooded, mailed figure with whatever is in its hands. Built to read as a silhouette
 * first: the hood point, the wide pauldrons and the weapon line are what identify it at
 * gameplay camera distance, so those are the largest shapes. Detail below about 4 cm is
 * wasted — the pixel post-process eats it.
 *
 * **Phase 41 — the geometry pass.** The same rig, the same silhouettes and the same painted
 * cells, on rounded parts: an ellipsoid chest, domed pauldrons, capsule limbs split at the
 * elbow and the knee, a sphere for a skull, a turned helm. Every number that a test measured
 * on the box body — the collider, the outline widths, the hem — was re-measured on this one,
 * and `silhouette.test.ts` says what moved. What did not move is the part count: a body is
 * still two dozen draw calls, because that, and not the triangle count, is what a pack of
 * thirty-two costs.
 *
 * The only warm metal on screen. Everything else in the world is bone, stone and rust, so the
 * bronze trim is what makes the player findable in a fight.
 */
export function buildCrusader(
  scene: Scene,
  palette: ModelPalette,
  isSelf: boolean,
  /**
   * The worn chest piece's visual class from the snapshot, or undefined for a bare
   * chest — which keeps exactly the body this model always had.
   */
  armour?: string,
  /**
   * The equipped main hand's `weapon_type` from the snapshot, or undefined for an
   * empty hand — which keeps the longsword this model always carried.
   */
  weapon?: string,
  /** The other four armour slots and the off hand. Absent means the defaults throughout. */
  gear: WornGear = {},
): Rig {
  const rig = createRig(scene, 'crusader', HIP_Y, HUMAN_LIMBS);

  // ── Torso: the armour layer ───────────────────────────────────────────────
  //
  // Cloth, leather and plate differ in width, shoulder mass and hem length; a bare
  // chest keeps the mail-and-tabard body this model started with. See armour.ts —
  // the shapes are a plain value there so they can be tested without a scene.
  buildArmour(scene, rig, palette, armourProfile(armour), isSelf ? 'selfGlow' : 'darkSteel');

  // ── Head: hood, cap or helm, from the head slot — or the chest when the head is bare ──
  //
  // Asked, on looking at it: *there is no helmet model?* There was not — every character wore
  // the same pointed cloth hood whatever they had on. The helm arrived derived from the chest,
  // because only the chest was on the wire; Phase 41 put the head slot on it, and the chest is
  // now only the fallback for a bare head. See `headStyleFor`.
  buildHead(scene, rig, palette, headStyleFor(gear.head, armour), FACE_TILT);

  // ── Limbs: two halves each, dressed by the hands, legs and feet slots ──────
  buildLimbs(scene, rig, palette, gear);

  // ── Weapon: in the right hand, at the end of the forearm ──────────────────
  //
  // The shapes live in weapons.ts, keyed off the snapshot's weapon type, and they go
  // into rig.heldParts rather than rig.parts so the collider stays the size of the
  // body — a staff rises 1.7 units above this hand.
  const held = weaponProfile(weapon);
  buildWeapon(scene, rig, palette, held);

  // ── Off hand: a shield, a tome, an orb, a dagger — and **only** if the main hand leaves
  // that arm free. A two-hander blocks the off-hand rather than occupying it, so the block
  // is derived from what is in the main hand; the *kind* is the wire's `offhand`, and an
  // absent one is the kite shield this model always carried.
  //
  // Into rig.heldParts, like the weapon: the shield is *held*, so if it were body the
  // collider would change with the weapon after all — a two-hander would drop it and
  // narrow the box. It is the one thing standing between "the collider ignores the
  // weapon" being exactly true and being nearly true.
  buildOffHand(scene, rig, palette, gear.offhand, held.twoHanded);

  // ── The cast glow: a light gathered in the off hand, grown only while casting ──
  //
  // Held, not body: it is drawn at scale zero almost always, and a collider must not be
  // measured from a thing that is usually not there. `ProceduralModel.glowFor` drives it.
  rig.castGlow = sphere(scene, rig.handL, rig.heldParts, {
    name: 'cru-castglow',
    size: [0.26, 0.26, 0.26],
    at: [-0.04, 0.02, 0.16],
    material: palette.get('ember'),
    segments: 6,
  });
  rig.castGlow.scaling.setAll(0);
  rig.castGlow.isVisible = false;

  return rig;
}
